import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { skip, takeUntil } from 'rxjs/operators';
import { Metadata, RunView } from '@memberjunction/core';
import { BaseResourceComponent } from '@memberjunction/ng-shared';
import { RegisterClass, UUIDsEqual } from '@memberjunction/global';
import { ResourceData, MJUserSettingEntity } from '@memberjunction/core-entities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { IzzyMetadataEngine } from 'mj_generatedentities';
import { IzzyContextService } from '../../../services/izzy-context.service';

/**
 * Persisted user state for the ticket workspace.
 */
interface TicketWorkspaceSettings {
  organizationId?: string;
}

/**
 * Ticket Workspace — top-level agent-facing Resource (PRD v4.0 §11).
 *
 * Tickets are day-to-day support work, NOT org administration. This Resource gives every
 * support agent (admin or not) a navigable destination for the ticket queue, alongside
 * Messages and Analytics. Admin-only ticket *management* (teams, SLA policies, routing
 * rules) stays under Administration in later phases.
 *
 * The Resource owns organization context (org selector + persistence) and hosts the
 * presentational `<izzy-ticket-queue>`, passing the selected org down. Opening a ticket
 * will route to the detail/timeline view (§11.3) in the next UI pass.
 */
@Component({
  standalone: false,
  selector: 'izzy-ticket-workspace',
  templateUrl: './ticket-workspace.component.html',
  styleUrls: ['./ticket-workspace.component.css'],
})
@RegisterClass(BaseResourceComponent, 'IzzyTicketWorkspaceResource')
export class IzzyTicketWorkspaceResource extends BaseResourceComponent implements OnInit, OnDestroy {

  async GetResourceDisplayName(data: ResourceData): Promise<string> {
    return 'Tickets';
  }

  async GetResourceIconClass(data: ResourceData): Promise<string> {
    return 'fa-solid fa-ticket';
  }

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private contextService: IzzyContextService
  ) {
    super();
  }

  // ========== Organization Context ==========
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected selectedOrg: mjBizAppsCommonOrganizationEntity | null = null;
  protected selectedOrganizationId: string | null = null;
  protected hasMultipleOrgs = false;

  // ========== UI State ==========
  protected isInitializing = true;
  /** When set, the detail/timeline view is shown for this ticket; otherwise the queue. */
  protected selectedIssueId: string | null = null;
  /** Initial assignee filter from a deep-link (analytics "Tickets by Assignee" drill-in):
   *  a Person ID or the literal 'unassigned'. Passed to the queue; null when not deep-linked. */
  protected assigneeFilter: string | null = null;

  // ========== User Settings Persistence ==========
  private userSettingsKey = 'Izzy.Tickets';
  private userSetting: MJUserSettingEntity | null = null;

  async ngOnInit(): Promise<void> {
    // Wire the base class's query-param subscriptions (popstate + reactive initial delivery) so
    // OnQueryParamsChanged fires — including on re-navigation to an already-open Tickets tab.
    super.ngOnInit();

    // Initial deep-link read — GetQueryParams() reads the tab's queryParams (the channel
    // OpenNavItemByName(..., {queryParams}) writes to). Mirrors base-admin-container's ngOnInit.
    this.applyAssigneeFilter(this.GetQueryParams()['assigneeFilter'] ?? null);

    await this.loadOrganizations();
    await this.applyInitialSelection();

    // Publish the org this section actually resolved to the shared context so the header
    // switcher shows what the page loaded. On a hard refresh the switcher boots first and seeds
    // "first org"; the old "only if empty" guard then let this section's URL/settings-driven org
    // differ from the pill indefinitely. The service's no-op setter prevents echo loops.
    if (this.selectedOrg) {
      this.contextService.selectedOrganizationId = this.selectedOrg.ID;
    }

    // React to org changes made in other Izzy sections while this tab stays alive.
    // skip(1) ignores the BehaviorSubject replay — the initial selection above
    // already consulted the context (with URL params taking precedence).
    this.contextService.orgChanges$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(orgId => this.onContextOrgChanged(orgId));

    this.isInitializing = false;
    this.NotifyLoadComplete();
  }

  /**
   * Apply an org change that originated in another Izzy section.
   * A null context (e.g. "All Organizations" in Analytics) is ignored
   * because the ticket queue always requires a concrete org.
   */
  private onContextOrgChanged(orgId: string | null): void {
    if (!orgId || UUIDsEqual(orgId, this.selectedOrg?.ID)) {
      return;
    }
    const org = this.organizations.find(o => UUIDsEqual(o.ID, orgId));
    if (org) {
      this.selectedIssueId = null; // org changed — an open ticket detail belongs to the old org
      this.onOrgSelectorChange(org, false);
    }
  }

  /**
   * Framework hook — called on browser back/forward AND when navigating to an ALREADY-OPEN Tickets
   * tab (e.g. clicking an assignee in analytics while Tickets is open). This is the canonical MJ
   * re-navigation channel (mirrors base-admin-container.OnQueryParamsChanged), so the queue re-filters
   * without a page refresh. ngOnInit's own GetQueryParams read and this stream replay can both fire on
   * first mount — applyAssigneeFilter is a no-op when the value is unchanged, covering that race.
   */
  override OnQueryParamsChanged(params: Record<string, string>, _source: 'popstate' | 'deeplink'): void {
    this.applyAssigneeFilter(params['assigneeFilter'] ?? null);
  }

  /** Set the assignee drill-in filter, showing the (filtered) queue. No-op when unchanged. */
  private applyAssigneeFilter(filter: string | null): void {
    if (filter === this.assigneeFilter) {
      return;
    }
    this.assigneeFilter = filter;
    if (filter) {
      this.selectedIssueId = null; // land on the filtered queue, not a previously open detail
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private async loadOrganizations(): Promise<void> {
    try {
      await IzzyMetadataEngine.Instance.Config(false);
      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();
      this.hasMultipleOrgs = this.organizations.length > 1;
    } catch (error) {
      console.error('Ticket Workspace: Error loading organizations:', error);
    }
  }

  /**
   * Determine the active organization: URL > shared context > user settings > first org.
   */
  private async applyInitialSelection(): Promise<void> {
    const urlOrgId = this.route.snapshot.queryParams['orgId'];
    // assigneeFilter is read reactively in ngOnInit's queryParams subscription (handles both initial
    // load and same-tab re-navigation from the analytics drill-in).

    let settings: TicketWorkspaceSettings | null = null;
    try {
      const md = new Metadata();
      const rv = new RunView();
      const result = await rv.RunView<MJUserSettingEntity>({
        EntityName: 'MJ: User Settings',
        ExtraFilter: `Setting = '${this.userSettingsKey}' AND UserID = '${md.CurrentUser.ID}'`,
        ResultType: 'entity_object',
      });
      if (result?.Success && result.Results.length > 0) {
        this.userSetting = result.Results[0];
        settings = JSON.parse(this.userSetting.Value || '{}') as TicketWorkspaceSettings;
      }
    } catch (error) {
      console.error('Ticket Workspace: Error loading user settings:', error);
    }

    let orgId: string | undefined = urlOrgId;
    if (!orgId && this.contextService.selectedOrganizationId) {
      orgId = this.contextService.selectedOrganizationId;
    }
    if (!orgId && settings?.organizationId) {
      orgId = settings.organizationId;
    }

    if (orgId) {
      const org = this.organizations.find(o => o.ID === orgId) || null;
      this.selectedOrg = org;
    }
    if (!this.selectedOrg && this.organizations.length > 0) {
      this.selectedOrg = this.organizations[0];
    }
    this.selectedOrganizationId = this.selectedOrg?.ID || null;

    this.updateUrl();
  }

  /**
   * Handler for the shared OrgSelectorComponent.
   *
   * `syncUrl` is false only for context-originated changes (another section's
   * org switch while this tab is hidden) — location.replaceState is global, so
   * a hidden tab must not rewrite the visible tab's URL.
   */
  onOrgSelectorChange(org: mjBizAppsCommonOrganizationEntity | null, syncUrl: boolean = true): void {
    this.selectedOrg = org;
    this.selectedOrganizationId = org?.ID || null;
    this.contextService.selectedOrganizationId = org?.ID || null;
    this.saveUserSettings();
    if (syncUrl) {
      this.updateUrl();
    }
  }

  private async saveUserSettings(): Promise<void> {
    try {
      const md = new Metadata();
      const settings: TicketWorkspaceSettings = {
        organizationId: this.selectedOrg?.ID,
      };
      if (!this.userSetting) {
        this.userSetting = await md.GetEntityObject<MJUserSettingEntity>('MJ: User Settings');
        this.userSetting.Setting = this.userSettingsKey;
        this.userSetting.UserID = md.CurrentUser.ID;
      }
      this.userSetting.Value = JSON.stringify(settings);
      await this.userSetting.Save();
    } catch (error) {
      console.error('Ticket Workspace: Error saving user settings:', error);
    }
  }

  /**
   * Open the ticket detail/timeline view (PRD §11.3) for the selected ticket. The detail
   * view replaces the queue within the workspace; `onCloseTicket` returns to the queue.
   */
  onOpenTicket(evt: { issueId: string }): void {
    this.selectedIssueId = evt.issueId;
  }

  /** Return from the ticket detail view to the queue. */
  onCloseTicket(): void {
    this.selectedIssueId = null;
  }

  private updateUrl(): void {
    const params = new URLSearchParams();
    if (this.selectedOrg) {
      params.set('orgId', this.selectedOrg.ID);
    }
    const path = this.location.path().split('?')[0];
    this.location.replaceState(path, params.toString());
  }
}

/**
 * Utility function to prevent tree-shaking.
 */
export function LoadTicketWorkspaceComponent(): void {
  // Intentionally empty
}
